import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Icon, PhoneInput, Tooltip } from '@column/column-ui-kit';

import { PendingIcon } from '~/elements';
import { useAddress } from '~/hooks';
import { RecaptchaAction, useReCAPTCHA } from '~/lib/recaptcha';
import { FactorType, FactorTypeResponse, UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { FieldStatus, FormElementAction, FormFieldStatus, FormFieldStatusWrapper } from '~/styles';
import { log } from '~/util';

import { AuthenticateModal } from './_components/AuthenticateModal';

interface Props {
  isTouched?: boolean;
  error?: string;
  onPhoneNumberChange: (phoneNumber: string) => void;
}

export const SectionMfaSms: React.FC<Props> = ({ onPhoneNumberChange, isTouched, error: errorProp }: Props) => {
  const [isAuthenticateModalOpen, setIsAuthenticateModalOpen] = useState(false);
  const { countries } = useAddress({ types: ['country'] });
  const { currentUser, refetchCurrentUser } = useSessionStore();
  const { addDangerNotification, addInfoNotification } = useNotificationStore();

  const closeModal = useCallback(() => {
    setIsAuthenticateModalOpen(false);
  }, []);

  const executeReCAPTCHA = useReCAPTCHA();

  const [phoneNumber, setPhoneNumber] = useState<string>('');

  useEffect(() => {
    if (currentUser) {
      setPhoneNumber(currentUser.phoneNumber ?? '');
    }
  }, [currentUser]);

  const setupSmsMfaMutation = useMutation({
    mutationFn: ({ reCaptchaToken, factorName }: { reCaptchaToken: string; factorName: string }) =>
      UserRepository.setupMfa(reCaptchaToken, {
        factorName,
        factorType: FactorType.SMS,
      }),
    onSuccess: () => {
      log({
        name: 'User enable MFA requested',
        context: currentUser,
      });

      addInfoNotification({
        content: 'Please confirm your phone number to finish 2FA setup.',
      });

      refetchCurrentUser();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Something went wrong, please try again.';
      addDangerNotification({
        content: message,
      });
    },
  });

  const handleEnableSmsMfa = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!currentUser) {
        return;
      }

      if (!phoneNumber) {
        addDangerNotification({
          content: 'Add a valid phone number first.',
        });
        return;
      }

      if (setupSmsMfaMutation.isPending) {
        return;
      }

      executeReCAPTCHA(RecaptchaAction.UpdateUser)
        .then((reCaptchaToken) =>
          currentUser
            .update({ phoneNumber }, reCaptchaToken)
            .then(() =>
              executeReCAPTCHA(RecaptchaAction.MfaSetup)
                .then((reCaptchaTokenMfa) => {
                  setupSmsMfaMutation.mutate({ reCaptchaToken: reCaptchaTokenMfa, factorName: phoneNumber });
                })
                .catch((err: Error) => {
                  addDangerNotification({
                    content: err.message,
                  });
                })
            )
            .catch((error: Error) => {
              addDangerNotification({
                content: error.message,
              });
            })
        )
        .catch((err: Error) => {
          addDangerNotification({
            content: err.message,
          });
        });
    },
    [
      currentUser,
      phoneNumber,
      setupSmsMfaMutation,
      addDangerNotification,
      executeReCAPTCHA,
      addInfoNotification,
      refetchCurrentUser,
    ]
  );

  const handleConfirmMfa = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsAuthenticateModalOpen(true);
    },
    [setIsAuthenticateModalOpen]
  );

  const isMfaPending = useMemo(
    () =>
      phoneNumber === currentUser?.phoneNumber &&
      currentUser?.factors.some((factor) => factor.factorType === FactorTypeResponse.SMS && !factor.factorVerified),
    [currentUser, phoneNumber]
  );

  const isMfaVerified = useMemo(
    () =>
      phoneNumber === currentUser?.phoneNumber &&
      currentUser?.factors.some((factor) => factor.factorType === FactorTypeResponse.SMS && factor.factorVerified),
    [currentUser, phoneNumber]
  );

  if (!currentUser) {
    return null;
  }

  return (
    <>
      <FormElementAction gap={!isMfaVerified || isMfaPending ? 8 : 0}>
        <FormFieldStatusWrapper>
          <PhoneInput
            value={phoneNumber}
            onChange={(value: string) => {
              onPhoneNumberChange(value);
              setPhoneNumber(value);
            }}
            placeholder="Phone number"
            countryList={countries}
            hasError={isTouched && !!errorProp}
          />
        </FormFieldStatusWrapper>

        {isMfaVerified ? (
          <FormFieldStatus>
            <FieldStatus show={isMfaVerified} color="success">
              Enabled
              <Icon.Checkmark />
            </FieldStatus>
          </FormFieldStatus>
        ) : isMfaPending ? (
          <FormFieldStatus>
            <FieldStatus show={isMfaPending}>
              Pending
              <PendingIcon />
            </FieldStatus>
          </FormFieldStatus>
        ) : (
          <FormFieldStatus>
            <FieldStatus color="warning">
              Not enabled
              <Icon.Warning />
            </FieldStatus>
          </FormFieldStatus>
        )}

        {isMfaPending ? (
          <Button onClick={handleConfirmMfa} icon={<Icon.CircleCheck />} variant="secondary" size="small">
            Confirm
          </Button>
        ) : (
          !isMfaVerified &&
          (currentUser.isEmailVerified ? (
            <Button onClick={handleEnableSmsMfa} icon={<Icon.PhoneAdd />} variant="secondary" size="small">
              Enable
            </Button>
          ) : (
            <Tooltip content="Verify email first">
              <Button icon={<Icon.PhoneAdd />} variant="secondary" isDisabled size="small">
                Enable
              </Button>
            </Tooltip>
          ))
        )}
      </FormElementAction>
      <AuthenticateModal isOpen={isAuthenticateModalOpen} onClose={closeModal} />
    </>
  );
};
